/*
Copyright 2026 Chain Care.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import type {
  ConfigOptions,
  PrenigmaCollaborationRuntimeConfigOptions,
} from "../config/ConfigOptions";
import {
  PRENIGMA_COLLABORATION_CANONICALIZATION_PROFILE,
  PRENIGMA_COLLABORATION_DIGEST_PROFILE,
  PRENIGMA_COLLABORATION_NAMESPACE,
  PRENIGMA_COLLABORATION_SIGNATURE_PROFILE,
  PRENIGMA_COLLABORATION_SOURCE_SYSTEM,
  PRENIGMA_COLLABORATION_VERSION,
} from "./collaborationRuntime";
import { PRENIGMA_STREAMING_VOICE_TURN_EVIDENCE_SCHEMA } from "./streamingVoiceTurnEvidence";

const PRENIGMA_COLLABORATION_RUNTIME_SERVICE =
  "prenigma.collaboration_runtime_bridge" as const;
const PRENIGMA_COLLABORATION_EVENT_SINK_BINDING =
  "prenigma.collaboration_runtime_bridge.source_event_ingest" as const;
const PRENIGMA_COLLABORATION_SOURCE_EVENTS_ROUTE_SUFFIX =
  "/demo-runtime-launchers/collaboration-runtime/source-events" as const;
const PRENIGMA_ELEMENT_CALL_LIVE_EVIDENCE_ROUTE_SUFFIX =
  "/demo-runtime-launchers/element-call-live-evidence" as const;
const REQUIRED_EVENT_TYPES = [
  "session",
  "command_receipt",
  "transcript",
  "barge_in",
  "tts_cancel",
  "no_transcript",
] as const;

export interface PrenigmaCollaborationRuntimeConfigSummary {
  enabled: boolean;
  service: string;
  sourceSystem: string;
  sourceNamespace: string;
  eventSinkBinding: string;
  eventSinkApiPath: string;
  eventSinkHttpMethod: string;
  timelineReadbackApiPath: string;
  /**
   * Where to obtain a central-custody signature. Read from backend_api rather
   * than hardcoded: the live-evidence harness hardcodes both paths, which is
   * why it is a harness and not a production template.
   */
  sourceSignatureApiPath: string;
  sourceSignatureHttpMethod: string;
  timelineReadbackHttpMethod: string;
  sourceEventApiConfigured: boolean;
  sourceEventsConfigured: boolean;
  sourceSigningRequired: boolean;
  signatureProfile: string;
  durableOutboxRequired: boolean;
  matrixReadbackRequired: boolean;
  streamingVoiceEvidenceConfigured: boolean;
  streamingVoiceEvidenceBackendApiConfigured: boolean;
  streamingVoiceEvidenceBackendApiPath: string;
  streamingVoiceEvidenceBackendHttpMethod: string;
  streamingVoiceEvidenceRequiredBeforeRelease: boolean;
  rawSecretMaterialReturned: boolean;
  requiresBootstrapInjection: boolean;
  liveEvidenceRequiredBeforeRelease: boolean;
}

export function summarizePrenigmaCollaborationRuntimeConfig(
  config: Pick<ConfigOptions, "prenigma_collaboration_runtime">,
): PrenigmaCollaborationRuntimeConfigSummary {
  const runtime = config.prenigma_collaboration_runtime;
  const source = runtime?.source;
  const delivery = runtime?.delivery;
  const signing = runtime?.signing;
  const matrixReadback = runtime?.matrix_readback;
  const runtimePolicy = runtime?.runtime_policy;
  const streamingVoiceEvidence = runtime?.evidence?.streaming_voice_turn;

  const enabled = runtime?.enabled === true;
  const sourceSystem = source?.system ?? "";
  const sourceNamespace = source?.namespace ?? "";
  const acceptedVersions = source?.accepted_versions ?? [];
  const emittedEvents = runtime?.events?.emitted ?? [];
  const eventSinkBinding = delivery?.event_sink_binding ?? "";
  const eventSinkApiPath = delivery?.event_sink_api_path ?? "";
  const eventSinkHttpMethod = delivery?.event_sink_http_method ?? "";
  const timelineReadbackApiPath = delivery?.timeline_readback_api_path ?? "";
  const backendApi = runtime?.backend_api;
  const sourceSignatureApiPath = backendApi?.signature_path ?? "";
  const sourceSignatureHttpMethod = backendApi?.signature_method ?? "POST";
  const timelineReadbackHttpMethod =
    delivery?.timeline_readback_http_method ?? "";
  const sourceEventApiConfigured =
    eventSinkApiPath.endsWith(
      PRENIGMA_COLLABORATION_SOURCE_EVENTS_ROUTE_SUFFIX,
    ) &&
    timelineReadbackApiPath === eventSinkApiPath &&
    eventSinkHttpMethod.toUpperCase() === "POST" &&
    timelineReadbackHttpMethod.toUpperCase() === "GET" &&
    delivery?.authenticated_product_studio_session_required === true;
  const sourceSigningRequired = signing?.required === true;
  const signatureProfile = signing?.signature_profile ?? "";
  const durableOutboxRequired = delivery?.durable_outbox_required === true;
  const matrixReadbackRequired = matrixReadback?.required === true;
  const streamingVoiceEvidenceConfigured =
    sourceEventApiConfigured &&
    runtime?.events?.streaming_voice_turn_evidence === true &&
    streamingVoiceEvidence?.schema ===
      PRENIGMA_STREAMING_VOICE_TURN_EVIDENCE_SCHEMA &&
    streamingVoiceEvidence?.host_slot === "streamingVoiceEvidence" &&
    streamingVoiceEvidence?.producer ===
      "connectPrenigmaStreamingVoiceEvidenceBridge" &&
    streamingVoiceEvidence?.backend_readback_required === true &&
    streamingVoiceEvidence?.raw_audio_persistence === false &&
    streamingVoiceEvidence?.raw_transcript_persistence === false &&
    streamingVoiceEvidence?.raw_secret_material_returned === false;
  const streamingVoiceEvidenceBackendApiPath =
    streamingVoiceEvidence?.backend_ingest_api_path ??
    deriveElementCallLiveEvidenceApiPath(eventSinkApiPath);
  const streamingVoiceEvidenceBackendHttpMethod =
    streamingVoiceEvidence?.backend_ingest_http_method ?? "POST";
  const streamingVoiceEvidenceBackendApiConfigured =
    streamingVoiceEvidenceConfigured &&
    streamingVoiceEvidenceBackendApiPath.endsWith(
      PRENIGMA_ELEMENT_CALL_LIVE_EVIDENCE_ROUTE_SUFFIX,
    ) &&
    streamingVoiceEvidenceBackendHttpMethod.toUpperCase() === "POST";

  return {
    enabled,
    service: runtime?.service ?? "",
    sourceSystem,
    sourceNamespace,
    eventSinkBinding,
    eventSinkApiPath,
    eventSinkHttpMethod,
    timelineReadbackApiPath,
    sourceSignatureApiPath,
    sourceSignatureHttpMethod,
    timelineReadbackHttpMethod,
    sourceEventApiConfigured,
    sourceEventsConfigured:
      enabled &&
      runtime?.service === PRENIGMA_COLLABORATION_RUNTIME_SERVICE &&
      sourceSystem === PRENIGMA_COLLABORATION_SOURCE_SYSTEM &&
      sourceNamespace === PRENIGMA_COLLABORATION_NAMESPACE &&
      acceptedVersions.includes(PRENIGMA_COLLABORATION_VERSION) &&
      REQUIRED_EVENT_TYPES.every((eventType) =>
        emittedEvents.includes(eventType),
      ) &&
      eventSinkBinding === PRENIGMA_COLLABORATION_EVENT_SINK_BINDING &&
      sourceEventApiConfigured &&
      durableOutboxRequired &&
      sourceSigningRequired &&
      signatureProfile === PRENIGMA_COLLABORATION_SIGNATURE_PROFILE &&
      signing?.canonicalization ===
        PRENIGMA_COLLABORATION_CANONICALIZATION_PROFILE &&
      signing?.digest === PRENIGMA_COLLABORATION_DIGEST_PROFILE &&
      signing?.raw_secret_material_required !== true &&
      signing?.raw_secret_material_returned !== true &&
      matrixReadbackRequired,
    sourceSigningRequired,
    signatureProfile,
    durableOutboxRequired,
    matrixReadbackRequired,
    streamingVoiceEvidenceConfigured,
    streamingVoiceEvidenceBackendApiConfigured,
    streamingVoiceEvidenceBackendApiPath,
    streamingVoiceEvidenceBackendHttpMethod,
    streamingVoiceEvidenceRequiredBeforeRelease:
      runtimePolicy?.streaming_voice_evidence_required_before_release === true,
    rawSecretMaterialReturned: signing?.raw_secret_material_returned === true,
    requiresBootstrapInjection:
      runtimePolicy?.requires_bootstrap_injection === true,
    liveEvidenceRequiredBeforeRelease:
      runtimePolicy?.live_evidence_required_before_release === true,
  };
}

export function isPrenigmaCollaborationRuntimeConfig(
  runtime: PrenigmaCollaborationRuntimeConfigOptions | undefined,
): runtime is PrenigmaCollaborationRuntimeConfigOptions {
  return summarizePrenigmaCollaborationRuntimeConfig({
    prenigma_collaboration_runtime: runtime,
  }).sourceEventsConfigured;
}

function deriveElementCallLiveEvidenceApiPath(
  sourceEventApiPath: string,
): string {
  if (
    !sourceEventApiPath.endsWith(
      PRENIGMA_COLLABORATION_SOURCE_EVENTS_ROUTE_SUFFIX,
    )
  ) {
    return "";
  }
  return `${sourceEventApiPath.slice(
    0,
    -PRENIGMA_COLLABORATION_SOURCE_EVENTS_ROUTE_SUFFIX.length,
  )}${PRENIGMA_ELEMENT_CALL_LIVE_EVIDENCE_ROUTE_SUFFIX}`;
}
